import React, { useEffect, useRef, useState } from "react";
import { getToken } from "../auth/auth";
import { RealtimeEvents } from "../realtime/events";
import { useRealtime, useRealtimeSocket } from "../realtime/useRealtime";
import {
  getAchievementUnlockedMessage,
  isAchievementUnlockedNotification,
  shouldShowAchievementConfetti,
} from "../utils/notifications";
import styles from "./AchievementUnlockNotifications.module.css";

type ConfettiBurst = {
  id: string;
};

type AchievementUnlockNotificationsProps = {
  toastApi: {
    success: (message: unknown, options?: Record<string, unknown>) => unknown;
  };
};

const CONFETTI_PIECES = Array.from({ length: 18 }, (_, index) => index);

export default function AchievementUnlockNotifications({
  toastApi,
}: AchievementUnlockNotificationsProps) {
  const { success } = toastApi;
  const realtime = useRealtimeSocket();
  const seenNotificationIdsRef = useRef(new Set() as Set<string>);
  const timeoutIdsRef = useRef([] as number[]);
  const [bursts, setBursts] = useState([] as ConfettiBurst[]);

  useRealtime(Boolean(getToken()));

  useEffect(() => {
    const handleItemCreated = (payload: any) => {
      const notification = payload?.notification;
      if (!notification || !isAchievementUnlockedNotification(notification)) return;

      const notificationId = String(notification?.id || "");
      if (notificationId && seenNotificationIdsRef.current.has(notificationId)) return;
      if (notificationId) {
        seenNotificationIdsRef.current.add(notificationId);
      }

      success(getAchievementUnlockedMessage(notification), {
        autoClose: 4500,
      });

      if (!shouldShowAchievementConfetti(notification)) return;

      const burstId = notificationId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setBursts((current: ConfettiBurst[]) => [...current, { id: burstId }]);

      const timeoutId = window.setTimeout(() => {
        setBursts((current: ConfettiBurst[]) =>
          current.filter((burst: ConfettiBurst) => burst.id !== burstId),
        );
      }, 1800);
      timeoutIdsRef.current.push(timeoutId);
    };

    realtime.on(RealtimeEvents.NotificationsItemCreated, handleItemCreated);

    return () => {
      realtime.off(RealtimeEvents.NotificationsItemCreated, handleItemCreated);
      timeoutIdsRef.current.forEach((timeoutId: number) => window.clearTimeout(timeoutId));
      timeoutIdsRef.current = [];
    };
  }, [realtime, success]);

  return (
    <div className={styles.layer} aria-hidden="true" data-testid="achievement-confetti-layer">
      {bursts.map((burst: ConfettiBurst) => (
        <div key={burst.id} className={styles.burst} data-testid="achievement-confetti-burst">
          {CONFETTI_PIECES.map((piece: number) => (
            <span
              key={`${burst.id}-${piece}`}
              className={styles.piece}
              style={
                {
                  "--piece-angle": `${piece * 20}deg`,
                  "--piece-delay": `${(piece % 6) * 35}ms`,
                  "--piece-distance": `${64 + (piece % 5) * 12}px`,
                } as any
              }
            />
          ))}
        </div>
      ))}
    </div>
  );
}
